import { ChangeDetectorRef, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { HotToastService } from '@ngxpert/hot-toast';
import { untilDestroyed } from '@ngneat/until-destroy';
import { AlertType, ChangeRequestStatus, HeadlineType, SectionType } from '@core/enums';
import { AuthenticationService, CredentialsService } from '@auth';
import { BankEntity, ChangeRequest, ClubEntity, Credentials, MemberEntity, SharingInfoEntity } from '@core/entities';
import { FormService } from '@core/services';
import { UseMyProfile } from '@core/usecases/useMyProfile';
import { debounceTime, finalize, forkJoin } from 'rxjs';
import { ClearPushNotificationSeen, DateTimeUtility, GenerateImageUrlWithToken, GetHeadlineOption, ibanValidator, mustMatch, normalizeIban } from '@core/utils';
import { ButtonComponent } from '@shared/components/ui';
import { SafeUrl } from '@angular/platform-browser';

interface ProfileDTO {
  city: string;
  postCode: string;
  street: string;
  street2: string;
  email: string;
  phone: string;
  birthDate: string;
}

/**
 * MyProfileFormComposition
 *
 * This class is a composition class for MyProfileComponent.
 * Composition classes are responsible for handling the business logic of the component and the data that is being displayed in the component.
 *
 * It is also responsible for handling the form validation and submission.
 * It is also responsible for handling the API calls and the data that is being fetched from the API.
 * It is also responsible for handling the alert messages and the alert types.
 * It is also responsible for handling the password change and the bank change.
 * It is also responsible for handling the birthday sharing and the advertisement sharing.
 */

export class MyProfileComposition extends FormService {
  clubInfo: ClubEntity;
  memberDetails: MemberEntity;
  passwordChangeForm: FormGroup;
  shareInfoForm: FormGroup;
  // these two properties `userDetails` and `qrImage` are only used in the digital card component
  userDetails: Credentials = null;
  qrImage: SafeUrl;
  maskedIBANDisplayValue = '';
  isValidatingIBAN = false;

  public readonly _translate: TranslateService = inject(TranslateService);

  protected readonly _credService: CredentialsService = inject(CredentialsService);
  protected readonly _cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  private readonly _router: Router = inject(Router);
  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _authService: AuthenticationService = inject(AuthenticationService);
  private readonly _toastService: HotToastService = inject(HotToastService);
  private readonly _useMyProfile = new UseMyProfile();

  private _formChangesBound = false;

  constructor() {
    super();

    this.userDetails = this._credService.credentials;
  }

  get isHeadlineEnterprise() {
    return GetHeadlineOption() === HeadlineType.ENTERPRISE;
  }

  get bankForm(): FormGroup {
    return this.form?.get('bankData') as FormGroup;
  }

  get isProfileFormInvalid(): boolean {
    return this._profileControls.some((control) => control.invalid);
  }

  get isProfileFormPristine(): boolean {
    return this._profileControls.every((control) => control.pristine);
  }

  get bankChangeRequest(): ChangeRequest<BankEntity> {
    return this.memberDetails?.changeRequest?.bank;
  }

  get bankAlertType(): AlertType {
    return this._getAlertType(this.bankChangeRequest);
  }

  get isBankChangeRequestPending(): boolean {
    return this.bankChangeRequest?.status === ChangeRequestStatus.Pending;
  }

  get bankSubmitButtonText(): string {
    return this._translate.instant(this.isBankChangeRequestPending ? 'Waiting for Approval' : 'users.change');
  }

  get memberChangeRequest(): ChangeRequest<MemberEntity> {
    return this.memberDetails?.changeRequest?.member;
  }

  get memberAlertType(): AlertType {
    return this._getAlertType(this.memberChangeRequest);
  }

  get isMemberChangeRequestPending(): boolean {
    return this.memberChangeRequest?.status === ChangeRequestStatus.Pending;
  }

  get profileSubmitButtonText(): string {
    return this._translate.instant(this.isMemberChangeRequestPending ? 'Waiting for Approval' : 'users.save profile');
  }

  get showMemberAlertCloseButton(): boolean {
    return this.memberAlertType === AlertType.REJECTED_REQUEST || this.memberAlertType === AlertType.APPROVED_REQUEST;
  }

  get showBankAlertCloseButton(): boolean {
    return this.bankAlertType === AlertType.REJECTED_REQUEST || this.bankAlertType === AlertType.APPROVED_REQUEST;
  }

  saveProfile(btnRef?: ButtonComponent): void {
    if (this.isMemberChangeRequestPending) {
      this._toastService.error(`${this._translate.instant('You already have a pending change request')}`);
      return;
    }
    // Only the profile section gates this save; `bankData` has its own form, validity rules and save button (`saveBank`),
    // so an invalid/unsupported IBAN must not block a postcode or phone change.
    if (this.isProfileFormInvalid) {
      this._toastService.error(`${this._translate.instant('Please fill all required fields')}`);
      this._markProfileControlsAsTouched();
      return;
    }
    const formData = this.removeEventFields(this.form.value);
    const originalData: MemberEntity = this.memberDetails;

    const changedFields: Partial<ProfileDTO> = {};

    // Check each field and only add changed ones
    if (this._valueChanged(originalData?.city, formData.city)) {
      changedFields.city = formData.city || null; // Ensure empty strings become null if needed
    }

    if (this._valueChanged(originalData?.postCode, formData.postCode)) {
      changedFields.postCode = formData.postCode || null;
    }

    if (this._valueChanged(originalData?.street, formData.street)) {
      changedFields.street = formData.street || null;
    }

    if (this._valueChanged(originalData?.street2, formData.street2)) {
      changedFields.street2 = formData.street2 || null;
    }

    if (this._valueChanged(originalData?.email, formData.email)) {
      changedFields.email = formData.email || null;
    }

    if (this._valueChanged(originalData?.phone, formData.phone)) {
      changedFields.phone = formData.phone || null;
    }

    if (this._valueChanged(originalData?.birthDate, formData.birthDate)) {
      changedFields.birthDate = formData.birthDate || null;
    }

    if (Object.keys(changedFields).length === 0) {
      this._toastService.info(`${this._translate.instant('No changes to save')}`);
      return;
    }

    this.call(this._useMyProfile.saveUserMemberProfile(changedFields as Partial<MemberEntity>), btnRef).then(
      () => {
        this._toastService.success(`${this._translate.instant('Update Successful')}`);
        this._disableFormFields();
        this.fetchData();
      },
      (err) => {
        if (typeof this.error === 'string') {
          this._toastService.error(this.error);
        } else {
          this._toastService.error(`${this._translate.instant('Something went wrong')}`);
        }
        this._disableFormFields();
      },
    );
  }

  saveBank(btnRef?: ButtonComponent) {
    if (this.isBankChangeRequestPending) {
      this._toastService.error(`${this._translate.instant('You already have a pending change request')}`);
      return;
    }
    if (!this.bankForm.valid || this.isValidatingIBAN) return;
    const body = this.bankForm.value;

    this.call(this._useMyProfile.saveBankInfo(body), btnRef).then(
      (res) => {
        if (res) {
          this._toastService.success(`${this._translate.instant('Update Successful')}`);
        }
        this._disableFormFields();
        this.fetchData();
      },
      (err) => {
        if (typeof this.error === 'string') {
          this._toastService.error(this.error);
        } else {
          this._toastService.error(`${this._translate.instant('Something went wrong')}`);
        }
        this.bankForm.get('iban').setValue(this.memberDetails?.bankData?.iban, { emitEvent: false });
        this.maskedIBANDisplayValue = this.maskIban(this.memberDetails?.bankData?.iban);
        this.bankForm.get('bic').setValue(this.memberDetails?.bankData?.bic, { emitEvent: false });
        this.bankForm.get('name').setValue(this.memberDetails?.bankData?.name, { emitEvent: false });
        this._disableFormFields();
      },
    );
  }

  changePassword(btnRef?: ButtonComponent): void {
    btnRef?.showLoading();
    const data = this.passwordChangeForm.value;
    this._useMyProfile
      .changeUserPassword(data)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          if (response) {
            this._authService
              .logout()
              .pipe(untilDestroyed(this))
              .subscribe({
                next: () => {
                  this._toastService.success(`${this._translate.instant('Password changed successfully. Please login again')}`);
                  this._credService.setCredentials();
                  ClearPushNotificationSeen();
                  btnRef?.showIdle();
                  this._router.navigate(['/login'], { queryParams: { returnUrl: this._router.url } });
                },
              });
          } else {
            btnRef?.showIdle();
          }
        },
        error: (e) => {
          this._toastService.error(e?.error?.message || `${this._translate.instant('Something went wrong')}`);
          btnRef?.showError();
        },
      });
  }

  fetchData(): void {
    const memberDetails$ = this._useMyProfile.getMemberProfile();
    const clubDetails$ = this._useMyProfile.getClubInfo();
    const sharingInfo$ = this._useMyProfile.getBirthdayAdvertisementSharing();

    this.isLoading = true;
    this.memberDetails = null;
    this.clubInfo = null;

    forkJoin([memberDetails$, clubDetails$, sharingInfo$])
      .pipe(untilDestroyed(this))
      .subscribe({
        next: async ([member, club, sharingInfo]) => {
          if (member?.membershipStartDate) member.membershipStartDate = DateTimeUtility.formatDateToString(member.membershipStartDate);
          // The API may return the IBAN grouped in fours (e.g. "DE89 3704 ..."); the validator works on the
          // compact form, so normalize before patching or a stored-but-valid IBAN shows up as an error.
          if (member?.bankData?.iban) member.bankData.iban = normalizeIban(member.bankData.iban);
          await this.updateAndValidate(this.form, member, null, false);
          this.memberDetails = member;
          this.clubInfo = club;
          this._checkSharingInfo(sharingInfo);
          // Awaited: it patches the pending change request over `member`, and `_syncBankFormState` below
          // derives the masked IBAN from the form, so it has to see the final value.
          await this._checkOfRequestedChanges(member);
          this.isLoading = false;

          this._syncBankFormState();
          this._bindFormChanges();
        },
      });
  }

  initForm(): void {
    this.form = this._fb.group({
      lastName: [''],
      firstName: [''],
      birthDate: ['', [Validators.required]],
      titleId: [''],
      title: [''],
      salutationId: [''],
      salutation: [''],
      letterSalutationId: [''],
      letterSalutation: [''],
      maritalStatusId: [''],
      maritalStatus: [''],
      emergencyContact: [''],
      emergencyPhone: [''],
      userRole: [{ value: '', disabled: true }],
      gender: [''],
      pobox: [''],
      poboxPostCode: [''],
      street: ['', [Validators.required]],
      street2: [''],
      poboxCity: [''],
      postCode: ['', [Validators.required, Validators.pattern('^[0-9]{5}(?:-[0-9]{4})?$')]],
      city: ['', [Validators.required]],
      country: [''],
      countryCode: [''],
      membershipNumber: [{ value: '', disabled: true }],
      membershipStartDate: [{ value: '', disabled: true }],
      bankData: this._fb.group({
        owner: [''],
        name: [''],
        iban: ['', [ibanValidator()]],
        // BIC/SWIFT is 8 or 11 characters (ISO 9362); the 11-char branch code is optional.
        bic: ['', [Validators.pattern('^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$')]],
      }),
      email: ['', [Validators.required]],
      phone: ['', [Validators.required, Validators.pattern('^[0-9-+()_{}]*$')]],
      memberPhoto: [''],
      moreInfo: [''],
    });

    this.passwordChangeForm = this._fb.group(
      {
        oldPassword: ['', [Validators.required]],
        newPassword: ['', [Validators.required]],
        confirmPassword: ['', [Validators.required]],
      },
      { validators: mustMatch('newPassword', 'confirmPassword') },
    );

    this.shareInfoForm = this._fb.group({
      shareBirthday: [false],
      allowAdvertis: [false],
    });
  }

  maskIban(iban: string): string {
    if (!iban || iban.length <= 4) return iban;
    return '*'.repeat(iban.length - 4) + iban.slice(-4);
  }

  onFocus(event: FocusEvent): void {
    if (this.isBankChangeRequestPending) return;
    const input = event.target as HTMLInputElement;
    input.value = this.form.get('bankData.iban')?.value || '';
  }

  onBlur(event: FocusEvent): void {
    if (this.isBankChangeRequestPending) return;
    const input = event.target as HTMLInputElement;
    // Users paste IBANs grouped in fours; store the compact form so the validator and the API agree.
    const fullIban = normalizeIban(input.value);
    const ibanControl = this.form.get('bankData.iban');
    ibanControl?.setValue(fullIban);
    ibanControl?.markAsTouched();
    ibanControl?.markAsDirty();
    this.maskedIBANDisplayValue = this.maskIban(fullIban);
    input.value = this.maskedIBANDisplayValue;
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  protected _fetchMemberDetails(): void {
    this._useMyProfile
      .getMemberProfile()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (member) => {
          this.memberDetails = member;
          this.isLoading = false;
        },
      });
  }

  protected _getUserDetails() {
    if (this.userDetails && (!this.userDetails.firstName || !this.userDetails.lastName)) {
      window.location.href = '/logout';
    }

    if (this.userDetails && this.userDetails.qrcodeUrl) {
      this.qrImage = GenerateImageUrlWithToken(this.userDetails?.userId, SectionType.QR_CODE);
      // this.qrImage = this._imageService.convertBase64ToBlobUrl(qrBase64Image.substring(20));
    } else {
      console.log('No QR code found');
    }
  }

  private _bindFormChanges(): void {
    // `fetchData()` runs again after every successful save, so without this guard each save would stack
    // another set of subscriptions and every later IBAN change would fire N lookups and N toasts.
    if (this._formChangesBound) return;
    this._formChangesBound = true;

    // bind to shareBirthday and allowAdvertis
    this.shareInfoForm
      .get('shareBirthday')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          const shareBirthday = value ? 0 : 1;
          this.call(this._useMyProfile.saveSharingBirthday(shareBirthday)).then(() => {
            this._toastService.success(`${this._translate.instant('Update Successful')}`);
          });
        },
      });

    this.shareInfoForm
      .get('allowAdvertis')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          const allowAdvertis = value ? 0 : 1;
          this.call(this._useMyProfile.saveAdvertisementView(allowAdvertis)).then(() => {
            this._toastService.success(`${this._translate.instant('Update Successful')}`);
            const creds = this._credService.credentials;
            creds.allowAdvertisement = allowAdvertis;
            this._credService.setCredentials(creds);
          });
        },
      });

    this.bankForm
      .get('iban')
      .valueChanges.pipe(untilDestroyed(this))
      .pipe(debounceTime(500))
      .subscribe({
        next: (iban: any) => {
          const ibanControl = this.bankForm.get('iban');
          // A structurally invalid IBAN is already flagged inline by `ibanValidator`, and the backend lookup
          // could not add anything to it, so skip the round-trip entirely. A disabled control means a pending
          // change request the user cannot edit — re-validating it would only replay a stale error toast.
          if (iban && ibanControl.enabled && !ibanControl.hasError('invalid')) {
            this.isValidatingIBAN = true;
            this._validateIBANAndUpdate();
          }
        },
      });
  }

  /**
   * Re-derived on every fetch, unlike the one-time subscriptions in `_bindFormChanges`: the masked display
   * value has to follow whatever the form now holds, and `FormService.call()` re-enables the whole form after
   * every save, which silently lifts the pending-request lock unless it is re-applied here.
   */
  private _syncBankFormState(): void {
    this.maskedIBANDisplayValue = this.maskIban(this.bankForm?.get('iban')?.value || '');

    // `emitEvent: false` matters: toggling a group emits `valueChanges` on its children, which would kick off
    // an IBAN lookup for the very request that is already awaiting approval.
    if (this.isBankChangeRequestPending) {
      this.bankForm?.disable({ emitEvent: false });
    } else {
      this.bankForm?.enable({ emitEvent: false });
    }
  }

  private _disableFormFields(): void {
    this.form.get('userRole').disable();
    this.form.get('membershipStartDate').disable();
    this.form.get('membershipNumber').disable();
  }

  /**
   * Overlays the values the user last submitted on top of the stored member record. Until a request is
   * approved the API still returns the *old* `bankData`/address, so without this the form snaps back to the
   * previous values the moment `fetchData()` runs after a save.
   */
  private async _checkOfRequestedChanges(memberDetails: MemberEntity): Promise<void> {
    const memberRequest = memberDetails?.changeRequest?.member;
    const bankRequest = memberDetails?.changeRequest?.bank;

    if (memberRequest?.status !== ChangeRequestStatus.Rejected && memberRequest?.dataChanges) {
      await this.updateAndValidate(this.form, memberRequest.dataChanges, null, false);
    }

    if (bankRequest?.status !== ChangeRequestStatus.Rejected && bankRequest?.dataChanges) {
      // `dataChanges` is a flat `Partial<BankEntity>`, so it has to be patched onto `bankForm` — patching it
      // onto `this.form`, where these controls live under `bankData`, silently matched nothing and left the
      // old IBAN in place next to the newly looked-up BIC and bank name.
      const bankChanges = { ...((bankRequest.dataChanges as any).bankData ?? bankRequest.dataChanges) };
      if (bankChanges.iban) bankChanges.iban = normalizeIban(bankChanges.iban);
      await this.updateAndValidate(this.bankForm, bankChanges, null, false);
    }

    // Explicitly mark the form as touched to show the validation errors
    this.form.markAllAsTouched();
  }

  private _getAlertType(changeRequest: ChangeRequest<MemberEntity | BankEntity>) {
    if (!changeRequest) return null;
    switch (changeRequest?.status) {
      case ChangeRequestStatus.Pending:
        return AlertType.PENDING_REQUEST;
      case ChangeRequestStatus.Rejected:
        return AlertType.REJECTED_REQUEST;
      case ChangeRequestStatus.Accepted:
        return AlertType.APPROVED_REQUEST;
      default:
        return null;
    }
  }

  private _checkSharingInfo(sharingInfo: SharingInfoEntity): void {
    if (sharingInfo) {
      this.shareInfoForm.get('shareBirthday').setValue(sharingInfo.shareBirthdayBool, {
        emitEvent: false,
      });
      this.shareInfoForm.get('allowAdvertis').setValue(sharingInfo.allowAdvertisementBool, {
        emitEvent: false,
      });
    }
  }

  private _validateIBANAndUpdate(): void {
    const ibanControl = this.bankForm.get('iban');

    this._useMyProfile
      .validateIBAN(ibanControl.value)
      // `finalize` rather than a `complete` handler: a failed lookup must also clear the flag, otherwise the
      // BIC and bank-name fields stay hidden behind the spinner and `saveBank` stays disabled.
      .pipe(
        finalize(() => (this.isValidatingIBAN = false)),
        untilDestroyed(this),
      )
      .subscribe({
        next: (response) => {
          if (response && response.valid) {
            ibanControl.setErrors(null);
            this.bankForm.get('bic').setValue(response.bic, { emitEvent: false });
            this.bankForm.get('name').setValue(response.name, { emitEvent: false });
          } else {
            // Structurally valid but the backend could not resolve the bank. That is a different failure
            // from a typo, so surface the backend's own wording instead of the generic "is invalid".
            ibanControl.setErrors({ dispatched: response?.message || this._translate.instant('formErrors.is invalid.') });
            this._toastService.error(response.message, { duration: 10000 });
          }
        },
        error: (e) => {
          this._toastService.error(e?.error?.message || `${this._translate.instant('Something went wrong')}`);
        },
      });
  }

  /**
   * The profile section only — `bankData` is excluded because it has its own validity rules and its own
   * save button (`saveBank`), so it must never gate saving a postcode or a phone number.
   */
  private get _profileControls(): AbstractControl[] {
    return Object.entries(this.form?.controls ?? {})
      .filter(([key]) => key !== 'bankData')
      .map(([, control]) => control);
  }

  private _markProfileControlsAsTouched(): void {
    this._profileControls.forEach((control) => control.markAsTouched());
  }

  // Helper function to safely compare values accounting for different types
  private _valueChanged(original: any, current: any): boolean {
    // If both values are empty (null, undefined, empty string), consider them equal
    if ((original == null || original === '') && (current == null || current === '')) return false;

    // If only one value is empty, they are different
    if (original == null || original === '' || current == null || current === '') return true;

    // Convert to strings for comparison if needed
    const originalStr = typeof original === 'string' ? original.trim() : String(original).trim();
    const currentStr = typeof current === 'string' ? current.trim() : String(current).trim();

    return originalStr !== currentStr;
  }
}
